import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import Seo, { eventSchema } from "@/components/Seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
    { value: "meet", label: "Meet" },
    { value: "cruise", label: "Cruise" },
    { value: "drag", label: "Drag Night" },
    { value: "carshow", label: "Car Show" },
    { value: "other", label: "Other" },
];

export default function Events() {
    const [events, setEvents] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [category, setCategory] = useState("");
    const { user } = useAuth();

    async function load() {
        try {
            const { data } = await api.get("/events", { params: category ? { category } : {} });
            setEvents(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    useEffect(() => { load(); }, [category]);

    async function toggleAttend(id) {
        if (!user) return toast.error("Sign in to RSVP");
        try {
            await api.post(`/events/${id}/attend`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <Layout>
            <Seo title="Events & Meets · SRT Finder" description="Local cruise-ins, drag nights, and car shows for Hellcat, Redeye, TRX and other SRT/R-T owners." canonicalPath="/events" jsonLd={events.slice(0, 10).map(eventSchema).filter(Boolean)} />
            <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="events-page">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <h1
                        className="text-3xl sm:text-4xl text-white"
                        style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}
                    >
                        <Calendar className="w-6 h-6 text-hellcat inline-block mr-2 mb-1" />
                        Events & Meets
                    </h1>
                    {user && (
                        <button
                            type="button"
                            onClick={() => setDialogOpen(true)}
                            data-testid="create-event-btn"
                            className="btn-red inline-flex items-center gap-2 px-4 py-2 text-sm font-bold"
                        >
                            <Plus className="w-4 h-4" /> New event
                        </button>
                    )}
                </div>

                <div className="flex gap-2 flex-wrap mb-5">
                    <button
                        onClick={() => setCategory("")}
                        data-testid="event-filter-all"
                        className={`chip ${!category ? "chip-active" : ""}`}
                    >
                        All
                    </button>
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.value}
                            onClick={() => setCategory(c.value)}
                            data-testid={`event-filter-${c.value}`}
                            className={`chip ${category === c.value ? "chip-active" : ""}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {events.length === 0 ? (
                    <div className="surface p-8 text-center" data-testid="events-empty">
                        <div className="text-lg text-white mb-1"
                             style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}>
                            No upcoming events
                        </div>
                        <p className="text-sm text-onyx-500">
                            {user ? "Be the first to post a meet." : "Sign in and post the first meet."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {events.map((ev) => (
                            <div key={ev.event_id} data-testid={`event-${ev.event_id}`}
                                 className="surface p-4 sm:p-5 rounded-2xl">
                                <div className="flex items-baseline justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="chip mr-2 uppercase text-[10px]">{ev.category}</span>
                                        <h2 className="text-lg sm:text-xl text-white inline"
                                            style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}>
                                            {ev.title}
                                        </h2>
                                    </div>
                                    <div className="text-[11px] uppercase tracking-widest text-hellcat font-bold shrink-0">
                                        {ev.event_date?.slice(0, 10)}
                                    </div>
                                </div>
                                <p className="text-sm text-white/85 mt-1.5 leading-relaxed">{ev.description}</p>
                                <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                                    <div className="text-xs text-onyx-500 inline-flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {ev.location}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-onyx-500 inline-flex items-center gap-1">
                                            <Users className="w-3 h-3" /> {ev.attending?.length || 0} going
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => toggleAttend(ev.event_id)}
                                            data-testid={`event-rsvp-${ev.event_id}`}
                                            className="btn-ghost px-3 py-1.5 text-xs font-bold"
                                        >
                                            {ev.attending?.includes(user?.user_id) ? "Going ✓" : "I'm going"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <CreateEventDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={load} />
        </Layout>
    );
}

function CreateEventDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({
        title: "", description: "", location: "", event_date: "", category: "meet",
    });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/events", form);
            toast.success("Event posted");
            onOpenChange(false);
            setForm({ title: "", description: "", location: "", event_date: "", category: "meet" });
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md surface" data-testid="create-event-dialog">
                <DialogHeader><DialogTitle className="text-white">New event</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <input required placeholder="Title (Cruise night at Cars & Coffee)"
                           value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                           className="field-input" data-testid="event-title-input" />
                    <select value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            className="field-input" data-testid="event-category-select">
                        {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <input required type="date" value={form.event_date}
                           onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                           className="field-input" data-testid="event-date-input" />
                    <input required placeholder="Location (city, state or venue name)"
                           value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                           className="field-input" data-testid="event-location-input" />
                    <textarea rows={3} placeholder="What's the plan?"
                              value={form.description}
                              onChange={(e) => setForm({ ...form, description: e.target.value })}
                              className="field-input" data-testid="event-description-input" />
                    <div className="flex justify-end">
                        <button type="submit" disabled={busy}
                                data-testid="event-submit-btn"
                                className="btn-red px-5 py-2.5 text-sm font-bold">
                            {busy ? "Posting…" : "Post event"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
